import React, { useState, useEffect } from "react";
import FadeLoader from "react-spinners/FadeLoader";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ValidationMessages from "../utils/validationMessages";
import "./SelectPricingPlan.css";

const SelectPricingPlan = ({
  setIsSelectPricingOpen,
  entityId,
  parkingLotId,
  pslId,
  groupName,
  currentPlanId,
  handleCancelSelectPricing,
  onConfirmSuccess,
}) => {
  const [rowsData, setRowsData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlanName, setSelectedPlanName] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [rowData, setRowData] = useState([]);
  const hostPath = process.env.REACT_APP_API_BASE_URL;
  const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;

  const weekOrder = [1, 2, 3, 4, 5, 6, 0];
  const dayMap = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday"
  };

  const formatDays = (arr) =>
    weekOrder.filter(d => arr.includes(d)).map(d => dayMap[d]).join(", ");
  const fetchPricingPlans = async () => {
    setLoader(true);
    try {
      const response = await fetch(`${hostPath}/api/GetAllPricingPlans`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": `${hostHeader}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const formattedData = data.map((item) => ({
        id: item.id,
        pricingPlanID: item.pricingPlanID,
        pricingPlanName: item.pricingPlanName,
        description: item.description,
        isDefault: item.isDefault,
        rules: item.rules,

        createdDate: item.createdDate,
        lastUpdatedDate: item.lastUpdatedDate
      }));
      setRowsData(formattedData);
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleRadioChange = (e, planId, planName) => {
    e.stopPropagation();
    setSelectedPlanId(planId);
    setSelectedPlanName(planName);
  };

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  useEffect(() => {
    if (currentPlanId) {
      setSelectedPlanId(currentPlanId);
    }
  }, [currentPlanId]);

  const handleSubmit = () => {
    if (!selectedPlanId) return;
    const selectedPlan = rowsData.find(
      (p) => p.pricingPlanID === selectedPlanId
    );

    const data = {
      message: ValidationMessages.confirmPricing,
      message2: ValidationMessages.confirmContinue,
      status: "confirm",
      val: selectedPlan,
    };
    setRowData(data);
    setOpen(true);
  };

  // ✅ FIXED: handleConfirm with PSL/group support
  const handleConfirm = async (text, status, val) => {
    setLoader(true);
    if (text === "confirm") {
      setOpen(false);
      setSubmitting(true);
      try {
        const payload = {
          EntityRegistrationID: entityId,
          PricingPlanID: selectedPlanId,
        };
        if (parkingLotId) payload.ParkingLotID = parkingLotId;
        if (pslId) payload.PSL_ID = pslId; // ✅ update single PSL
        if (groupName) payload.GroupName = groupName; // ✅ update specific group
        const response = await fetch(`${hostPath}/api/AssignPricingPlan`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-functions-key": `${hostHeader}`,
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        // ✅ Notify parent so it updates UI
        if (onConfirmSuccess) {
          onConfirmSuccess({
            planId: selectedPlanId,
            planName: selectedPlanName,
          });
        }
        setIsSelectPricingOpen(false);
        setSelectedPlanId(null);
      } catch (error) {
        console.error("Error assigning pricing plan:", error);
      } finally {
        setSubmitting(false);
        setLoader(false);
      }
    } else if (text === "cancel") {
      setOpen(false);
      setLoader(false);
    }
  };

  const getPriceRange = (rules) => {
    if (!rules || rules.length === 0) return "—";
    const prices = rules.map(r => Number(r.price));
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? `$${min}` : `$${min} - $${max}`;
  };


  return (
    <>
      {loader && (
        <div className="loader-overlay">
          <FadeLoader />
        </div>
      )}

      <div className="select-pricing-container">
        <div className="select-pricing-header">
          <h5 style={{ color: "black" }}>Select Pricing Plan</h5>
        </div>
      </div>
      <TableContainer
        component={Paper}
        sx={{ maxHeight: "505px", overflow: "auto", width: "900px" }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>

          {/* HEADER */}
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "5%" }} />
              <TableCell sx={{ width: "25%" }}>Pricing Plan</TableCell>
              <TableCell sx={{ width: "30%" }}>Description</TableCell>
              <TableCell sx={{ width: "20%" }}>Pricing Rules</TableCell>
              <TableCell sx={{ width: "20%" }}>Price Range</TableCell>
            </TableRow>
          </TableHead>

          {/* BODY */}
          <TableBody>
            {rowsData.length > 0 ? (
              rowsData.map((row) => (
                <TableRow
                  key={row.pricingPlanID}
                  hover
                  onClick={() =>
                    handleRadioChange(null, row.pricingPlanID, row.pricingPlanName)
                  }
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedPlanId === row.pricingPlanID
                        ? "#f5f7ff"   // light highlight
                        : "inherit",
                  }}
                >
                  {/* RADIO */}
                  <TableCell align="center">
                    <input
                      type="radio"
                      name="selectedPlan"
                      checked={selectedPlanId === row.pricingPlanID}
                      onChange={(e) =>
                        handleRadioChange(
                          e,
                          row.pricingPlanID,
                          row.pricingPlanName
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        transform: "scale(1.2)",
                        cursor: "pointer",
                      }}
                    />
                  </TableCell>

                  {/* PLAN NAME */}
                  <TableCell>
                    {row.pricingPlanName}
                    {row.isDefault && (
                      <span className="default-badge">Default</span>
                    )}
                  </TableCell>

                  {/* DESCRIPTION */}
                  <TableCell align="center">
                    {row.description || "—"}
                  </TableCell>

                  {/* RULE COUNT */}
                  <TableCell align="center">
                    {`${row.rules?.length || 0} ${row.rules?.length === 1 ? "Rule" : "Rules"
                      }`}
                  </TableCell>

                  {/* PRICE RANGE */}
                  <TableCell align="center">
                    {getPriceRange(row.rules)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No Pricing Plans Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>
      </TableContainer>



      <div className="select-pricing-buttons">
        <button className="cancel-btn" onClick={handleCancelSelectPricing}>
          Cancel
        </button>
        <button
          className="select-pricing-btn"
          onClick={handleSubmit}
          disabled={submitting || !selectedPlanId}
          style={{
            cursor: submitting || !selectedPlanId ? "not-allowed" : "pointer",
          }}
        >
          Confirm
        </button>
      </div>

      <Dialog
        classes={{ paper: "custom-paper-class-delete" }}
        open={open}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "start",
            color: "#000",
            fontSize: "24px",
            fontWeight: "700",
            padding: "5px 24px",
          }}
        >
          {rowData?.message}
        </DialogTitle>
        <DialogContent style={{ border: "none", padding: "5px 22px" }}>
          <Typography gutterBottom>
            <span>{rowData?.message2}</span>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "right" }}>
          <Button className="custom-button-cancel" onClick={() => handleConfirm("cancel")}>
            Cancel
          </Button>
          <Button
            className="custom-button-delete"
            onClick={() =>
              handleConfirm("confirm", rowData?.status, rowData?.val)
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectPricingPlan;
